import React, { useState, useEffect } from 'react';
import axios from 'axios';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { useFees } from '../context/FeeContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Settings = ({ user, onLogout, onUpdate }) => {
  const { feeRates } = useFees();
  const [currencies, setCurrencies] = useState([]);
  const [selectedCurrency, setSelectedCurrency] = useState(user?.currency || 'USD');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    fetchCurrencies();
  }, []);

  const fetchCurrencies = async () => {
    try {
      const response = await axios.get(`${API}/currencies`);
      setCurrencies(response.data.currencies);
    } catch (error) {
      console.error('Error fetching currencies:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCurrencyUpdate = async () => {
    setSaving(true);
    setSuccess(false);

    try {
      await axios.put(`${API}/user/currency?currency=${selectedCurrency}`);
      
      // Fetch updated user data
      const userResponse = await axios.get(`${API}/auth/me`);
      onUpdate(userResponse.data);
      
      setSuccess(true);
      setTimeout(() => setSuccess(false), 3000);
    } catch (error) {
      console.error('Error updating currency:', error);
      alert('Failed to update currency');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Navbar user={user} onLogout={onLogout} />
        <div className="flex items-center justify-center h-96">
          <div className="text-xl font-semibold text-gray-600">Loading...</div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50" data-testid="settings-page">
      <Navbar user={user} onLogout={onLogout} />

      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <h1 className="text-3xl font-bold mb-8 bg-gradient-to-r from-orange-500 to-orange-600 bg-clip-text text-transparent">
          Settings
        </h1>

        {/* Currency Settings */}
        <div className="bg-white rounded-lg shadow-md p-6 mb-6">
          <h2 className="text-xl font-bold mb-4">Currency Preferences</h2>
          
          {success && (
            <div className="mb-4 p-3 bg-green-100 border border-green-400 text-green-700 rounded" data-testid="success-message">
              Currency updated successfully!
            </div>
          )}

          <div className="mb-6">
            <label className="block text-gray-700 font-semibold mb-2">
              Preferred Currency
            </label>
            <p className="text-sm text-gray-600 mb-4">
              This will be used for displaying prices and your earnings/payments across the platform.
            </p>
            <select
              value={selectedCurrency}
              onChange={(e) => setSelectedCurrency(e.target.value)}
              className="w-full md:w-1/2 px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              data-testid="currency-select"
            >
              {currencies.map((currency) => (
                <option key={currency} value={currency}>
                  {currency}
                </option>
              ))}
            </select>
          </div>

          <button
            onClick={handleCurrencyUpdate}
            disabled={saving || selectedCurrency === user?.currency}
            className="bg-gradient-to-r from-orange-500 to-orange-600 text-white px-6 py-2 rounded-lg hover:shadow-lg font-semibold disabled:opacity-50 disabled:cursor-not-allowed transition"
            data-testid="save-currency-button"
          >
            {saving ? 'Saving...' : 'Save Currency'}
          </button>
        </div>

        {/* Supported Currencies Info */}
        <div className="bg-gradient-to-r from-orange-50 to-orange-100 p-6 rounded-lg border border-blue-200">
          <h3 className="font-bold text-lg mb-3">Supported Currencies</h3>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
            {currencies.map((currency) => (
              <div key={currency} className="bg-white px-4 py-2 rounded shadow-sm">
                <span className="font-semibold">{currency}</span>
              </div>
            ))}
          </div>
          <p className="text-sm text-gray-600 mt-4">
            <strong>Note:</strong> Changing your currency will affect how prices are displayed, but actual payment amounts will remain in the original currency of the service provider.
          </p>
        </div>

        {user.role === 'provider' && (
          <div className="bg-yellow-50 p-6 rounded-lg border border-yellow-200 mt-6">
            <div className="flex items-start gap-3">
              <svg className="w-6 h-6 text-yellow-600 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"></path>
              </svg>
              <div>
                <h4 className="font-bold text-yellow-800 mb-2">Provider Currency Information</h4>
                <p className="text-sm text-yellow-700">
                  As a service provider, your services will be listed in your selected currency. Clients will see prices in your currency when booking your services. Commission ({feeRates.provider_fee_rate}%) will be deducted in your currency, and you'll receive {100 - feeRates.provider_fee_rate}% of the booking amount.
                </p>
              </div>
            </div>
          </div>
        )}
      </div>

      <Footer />
    </div>
  );
};

export default Settings;
